import { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  AppShell,
  NavLink,
  Group,
  Text,
  Button,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import {
  IconLayoutDashboard,
  IconUsers,
  IconChecklist,
  IconChartBar,
  IconLogout,
  IconListDetails,
  IconMusic,
  IconChevronLeft,
  IconChevronRight,
  IconWallet,
  IconCoins,
  IconFilter,
  IconMail,
  IconSettings,
  IconShieldLock,
  IconSpeakerphone,
  IconBell,
  IconBrandInstagram,
  IconTrophy,
  IconTrash,
  IconMessageHeart,
  IconPhoto,
  IconShieldHalf,
  IconUsersGroup,
  IconDeviceMobile,
  IconReportAnalytics,
  IconShieldCheck,
  IconAdjustments,
} from "@tabler/icons-react";
import { useAuth } from "../../hooks/useAuth";
import { AdminPermissions } from "../../lib/data";
import "./styles.css";

const Dashboard = () => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, admin } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const navItems = [
    { label: "Overview", icon: IconLayoutDashboard, path: "/d" },
    { label: "Members", icon: IconUsers, path: "/d/members", permissions: [AdminPermissions.MANAGE_USERS, AdminPermissions.MANAGE_VERIFICATION] },
    { label: "Access", icon: IconShieldCheck, path: "/d/access", permissions: [AdminPermissions.MANAGE_USERS] },
    { label: "Content", icon: IconPhoto, path: "/d/content", permissions: [AdminPermissions.MANAGE_MISSIONS] },
    { label: "Review", icon: IconChecklist, path: "/d/review", permissions: [AdminPermissions.MANAGE_MISSIONS, AdminPermissions.MANAGE_PROOFS] },
    { label: "Money", icon: IconWallet, path: "/d/money", permissions: [AdminPermissions.MANAGE_WITHDRAWALS] },
    { label: "Growth", icon: IconChartBar, path: "/d/growth", permissions: [AdminPermissions.MANAGE_USERS] },
    { label: "System", icon: IconSettings, path: "/d/system", permissions: [AdminPermissions.MANAGE_ADMINS, AdminPermissions.MANAGE_CONFIG, AdminPermissions.VIEW_AUDIT_LOGS, AdminPermissions.MANAGE_USERS] },
    // Restricted to specific PEOPLE (by email), not a role — hidden from everyone
    // else, including super_admin. Must match the server allow-list.
    { label: "Verification", icon: IconShieldCheck, path: "/d/verification", emails: ["anointedexcel59@gmail.com", "fasanfavour123@gmail.com"] },
    { label: "Control Center", icon: IconAdjustments, path: "/d/control-center", emails: ["anointedexcel59@gmail.com", "fasanfavour123@gmail.com"] },
  ];

  const filteredNavItems = navItems.filter((item) => {
    // Email allow-list wins over everything (even super_admin).
    const emails: string[] | undefined = (item as any).emails;
    if (emails)
      return emails.includes(String(admin?.email || "").toLowerCase());
    const perms: string[] =
      (item as any).permissions ||
      ((item as any).permission ? [(item as any).permission] : []);
    if (perms.length === 0) return true;
    if (admin?.role === "super_admin") return true;
    return perms.some((p) => admin?.permissions?.includes(p));
  });

  return (
    <AppShell
      navbar={{
        width: 300,
        breakpoint: "sm",
      }}
      padding="md"
      className="admin-dashboard"
    >
      <AppShell.Navbar
        className={`dashboard-sidebar ${collapsed ? "collapsed" : ""}`}
        style={{ width: collapsed ? 80 : 300 }}
      >
        <div className="sidebar-header">
          {!collapsed ? (
            <>
              <Group gap="xs" className="sidebar-logo" justify="space-between">
                <Group gap="xs">
                  <img src="/icon.png" alt="Virel" width={24} height={24} />
                  <Text fw={600} size="lg" className="sidebar-logo-text">
                    Virel
                  </Text>
                </Group>
                <ActionIcon
                  variant="subtle"
                  onClick={() => setCollapsed(!collapsed)}
                  className="sidebar-toggle"
                >
                  <IconChevronLeft size={18} />
                </ActionIcon>
              </Group>
              <Text size="xs" c="dimmed" className="sidebar-subtitle">
                Admin Dashboard
              </Text>
            </>
          ) : (
            <Group gap="xs" justify="center" className="sidebar-logo">
              <img src="/icon.png" alt="Virel" width={24} height={24} />
              <ActionIcon
                variant="subtle"
                onClick={() => setCollapsed(!collapsed)}
                className="sidebar-toggle"
                style={{ position: "absolute", right: 8 }}
              >
                <IconChevronRight size={18} />
              </ActionIcon>
            </Group>
          )}
        </div>

        <div className="sidebar-nav">
          {filteredNavItems.map((item) => {
            const Icon = item.icon;
            const isActive =
              location.pathname === item.path ||
              location?.pathname?.startsWith(`${item.path}/`);

            return (
              <Tooltip
                key={item.path}
                label={item.label}
                position="right"
                disabled={!collapsed}
                withArrow
              >
                <NavLink
                  label={item.label}
                  leftSection={<Icon size={18} />}
                  active={isActive}
                  href={item.path}
                  onClick={() => navigate(item.path)}
                  className={`sidebar-nav-item ${isActive ? "active" : ""} ${
                    collapsed ? "collapsed" : ""
                  }`}
                />
              </Tooltip>
            );
          })}
        </div>

        <div className="sidebar-profile">
          <Text fw={600} size="sm">
            {admin?.firstName || "Admin"}
          </Text>
          <Text size="xs" c="dimmed">
            {admin?.email || "Signed in"}
          </Text>
        </div>

        <div className="sidebar-footer">
          <Tooltip
            label="Logout"
            position="right"
            disabled={!collapsed}
            withArrow
          >
            <Button
              variant="subtle"
              color="red"
              leftSection={<IconLogout size={18} />}
              onClick={handleLogout}
              fullWidth={!collapsed}
              className={`sidebar-logout ${collapsed ? "collapsed" : ""}`}
            >
              {!collapsed && "Logout"}
            </Button>
          </Tooltip>
        </div>
      </AppShell.Navbar>

      <AppShell.Main
        className={`dashboard-main ${collapsed ? "collapsed" : ""}`}
      >
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
};

export default Dashboard;
